// ─── Types ────────────────────────────────────────────────────────────────────

export interface TypewriterOptions {
  /**
   * Milliseconds between ticks. Default: 16 (~60 fps).
   * Lower = faster animation.
   */
  intervalMs?: number;

  /**
   * Characters to reveal per tick. Default: 3.
   * Combine with `intervalMs` to control perceived speed:
   *   3 chars × 16 ms ≈ 187 chars/sec  ≈ comfortable reading speed
   *   1 char  × 16 ms ≈  62 chars/sec  ≈ dramatic typewriter
   *   6 chars × 16 ms ≈ 375 chars/sec  ≈ near-instant
   */
  charsPerTick?: number;

  /** Called on every tick with the text revealed so far. */
  onUpdate: (displayed: string) => void;

  /** Called once when the queue is empty and no more input is expected. */
  onComplete?: () => void;
}

export interface Typewriter {
  /**
   * Append a new chunk of text to the queue.
   * Safe to call at any time — including before the interval fires.
   */
  push: (chunk: string) => void;

  /**
   * Signal that no more chunks will arrive.
   * The typewriter will call `onComplete` once the queue is fully drained.
   */
  seal: () => void;

  /**
   * Stop immediately, clear the queue, and suppress further callbacks.
   * Use this to cancel on component unmount or user abort.
   */
  cancel: () => void;

  /** Force-flush the remaining queue instantly (e.g. on user click). */
  flush: () => void;
}

// ─── Factory ──────────────────────────────────────────────────────────────────

export function createTypewriter(options: TypewriterOptions): Typewriter {
  const {
    intervalMs = 16,
    charsPerTick = 3,
    onUpdate,
    onComplete,
  } = options;

  let queue = '';       // pending characters not yet revealed
  let displayed = '';   // characters shown so far
  let sealed = false;   // no more push() calls expected
  let cancelled = false;
  let timer: ReturnType<typeof setTimeout> | null = null;

  // ── Human-ish pacing ─────────────────────────────────────────────────────
  // A perfectly steady interval reads as mechanical. Real typing (and token
  // streaming) varies tick-to-tick and pauses slightly longer after
  // punctuation — this jitters both the delay and the reveal chunk size.

  function jitterDelay(base: number) {
    return base * (0.4 + Math.random() * 1.3); // ~0.4x – 1.7x
  }

  function jitterChars(base: number) {
    return Math.max(1, Math.round(base * (0.4 + Math.random() * 1.4))); // ~0.4x – 1.8x
  }

  function pauseAfter(char: string | undefined) {
    if (!char) return 0;
    if (/[.!?]/.test(char)) return jitterDelay(intervalMs) * 6;
    if (/[,;:—]/.test(char)) return jitterDelay(intervalMs) * 3;
    return 0;
  }

  // ── Tick ───────────────────────────────────────────────────────────────────

  function tick() {
    timer = null;
    if (cancelled) return;

    if (queue.length === 0) {
      // Nothing to reveal yet — wait for more pushes
      if (sealed) complete();
      return;
    }

    // Reveal the next chunk (size varies), but never cut in the middle of an HTML tag
    const slice = safeSlice(queue, jitterChars(charsPerTick));
    queue = queue.slice(slice.length);
    displayed += slice;

    onUpdate(displayed);

    if (queue.length === 0 && sealed) {
      complete();
      return;
    }

    timer = setTimeout(tick, jitterDelay(intervalMs) + pauseAfter(slice[slice.length - 1]));
  }

  function complete() {
    stop();
    onComplete?.();
  }

  function stop() {
    if (timer !== null) {
      clearTimeout(timer);
      timer = null;
    }
  }

  function start() {
    if (timer === null && !cancelled) {
      timer = setTimeout(tick, jitterDelay(intervalMs));
    }
  }

  // ── Public API ─────────────────────────────────────────────────────────────

  const push = (chunk: string) => {
    if (cancelled || sealed) return;
    queue += chunk;
    start(); // no-op if already running
  };

  const seal = () => {
    if (cancelled) return;
    sealed = true;
    // If queue already empty, complete right away
    if (queue.length === 0) {
      stop();
      onComplete?.();
    }
    // Otherwise the running tick will call complete() when queue drains
  };

  const cancel = () => {
    cancelled = true;
    stop();
    queue = '';
  };

  const flush = () => {
    if (cancelled) return;
    stop();
    displayed += queue;
    queue = '';
    onUpdate(displayed);
    if (sealed) onComplete?.();
  };

  return { push, seal, cancel, flush };
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

/**
 * Slice up to `n` characters from `text` without cutting inside an HTML tag.
 *
 * If the slice would land inside `<...>`, we extend it to the closing `>` so
 * that `dangerouslySetInnerHTML` always receives well-formed partial HTML.
 *
 * Examples:
 *   safeSlice("Hello <b>world", 8)  → "Hello <b" — oops, cut mid-tag
 *   safeSlice("Hello <b>world", 8)  → "Hello <b>" — extended to close tag
 */
function safeSlice(text: string, n: number): string {
  if (n >= text.length) return text;

  let slice = text.slice(0, n);

  // Check if we're inside an open HTML tag
  const openBracket = slice.lastIndexOf('<');
  if (openBracket !== -1) {
    // If there's no matching '>' after the '<' in our slice, we're mid-tag
    const closeInSlice = slice.indexOf('>', openBracket);
    if (closeInSlice === -1) {
      // Find the '>' in the remainder and extend
      const closeInRemainder = text.indexOf('>', n);
      if (closeInRemainder !== -1) {
        slice = text.slice(0, closeInRemainder + 1);
      } else {
        // Malformed / unclosed tag — just skip to the end of remaining text
        slice = text;
      }
    }
  }

  return slice;
}
